import React, { useState, useMemo } from 'react';
import { Modal, Table, Button, Image, Typography, Space, Tag, Card, Checkbox, Select, Segmented, Tooltip, Popover } from 'antd';
import { AppstoreOutlined, UnorderedListOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import type { ColumnsType } from 'antd/es/table';

// Image with hover preview using Popover (renders in portal, no overflow issues)
const ImageWithPreview: React.FC<{
  src?: string;
  width: number;
  height: number;
  previewSize?: number;
}> = ({ src, width, height, previewSize = 250 }) => {
  if (!src) return null;
  return (
    <Popover
      content={
        <img
          src={src}
          alt=""
          style={{
            maxWidth: previewSize,
            maxHeight: previewSize,
            objectFit: 'contain',
            display: 'block',
          }}
        />
      }
      placement="right"
      mouseEnterDelay={0.2}
      overlayInnerStyle={{ padding: 4 }}
      zIndex={1200}
    >
      <div style={{ cursor: 'pointer', display: 'inline-block' }}>
        <Image
          src={src}
          width={width}
          height={height}
          style={{ objectFit: 'contain' }}
          preview={false}
          fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII="
        />
      </div>
    </Popover>
  );
};

export interface Competitor {
  asin: string;
  title?: string;
  image?: string;
  price?: number;
  brand?: string;
  rating?: string;
  reviews?: string;
  bsr?: string;
  clickShare?: number;
  monthlySales?: number;
}

export interface Niche {
  nicheId: string;
  nicheTitle: string;
  products: Competitor[];
  count: number;
}

interface CompetitorModalProps {
  open: boolean;
  niches?: Niche[];
  competitors: Competitor[];
  loading?: boolean;
  onClose: () => void;
  onSelect: (asins: string[]) => void;
  sourceAsin?: string;
  nicheTitle?: string;
  totalFound?: number;
  alreadySelectedAsins?: string[];
}

type ViewMode = 'carousel' | 'table';
type SortBy = 'clickShare' | 'price' | 'reviews' | 'bsr' | 'rating';

const sortProducts = (products: Competitor[], sortBy: SortBy): Competitor[] => {
  return [...products].sort((a, b) => {
    switch (sortBy) {
      case 'clickShare':
        return (b.clickShare || 0) - (a.clickShare || 0);
      case 'price':
        return (a.price || 0) - (b.price || 0);
      case 'reviews':
        return Number(b.reviews || 0) - Number(a.reviews || 0);
      case 'bsr':
        return Number(a.bsr || 999999) - Number(b.bsr || 999999);
      case 'rating':
        return Number(b.rating || 0) - Number(a.rating || 0);
      default:
        return 0;
    }
  });
};

const ProductCard: React.FC<{
  product: Competitor;
  selected: boolean;
  onToggle: (asin: string) => void;
}> = ({ product, selected, onToggle }) => {
  const { t } = useTranslation();
  return (
  <Card
    size="small"
    hoverable
    onClick={() => onToggle(product.asin)}
    style={{
      width: 160,
      flexShrink: 0,
      border: selected ? '2px solid #1890ff' : '1px solid #f0f0f0',
      background: selected ? '#e6f4ff' : '#fff',
    }}
    styles={{ body: { padding: 8 } }}
  >
    <div style={{ position: 'relative' }}>
      <Checkbox
        checked={selected}
        style={{ position: 'absolute', top: 0, left: 0, zIndex: 1 }}
        onClick={(e) => e.stopPropagation()}
        onChange={() => onToggle(product.asin)}
      />
      <div onClick={(e) => e.stopPropagation()}>
        <ImageWithPreview src={product.image} width={140} height={100} previewSize={280} />
      </div>
    </div>
    <Typography.Text
      copyable={{ text: product.asin }}
      style={{ fontSize: 9, color: '#1890ff', display: 'block', marginTop: 4 }}
    >
      {product.asin}
    </Typography.Text>
    <Tooltip title={product.title} placement="top" mouseEnterDelay={0.3}>
      <Typography.Text
        ellipsis
        style={{ fontSize: 10, display: 'block', height: 28, lineHeight: '14px', cursor: 'default' }}
      >
        {product.title || '—'}
      </Typography.Text>
    </Tooltip>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
      <Typography.Text type="secondary" style={{ fontSize: 10 }}>
        ~${product.price?.toFixed(2) || '—'}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 10 }}>
        {product.rating ? `★${product.rating}` : ''}
      </Typography.Text>
    </div>
    <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 2 }}>
      <Typography.Text type="secondary" style={{ fontSize: 9 }}>
        {product.reviews ? t('idea_calc.competitor_modal.card_reviews_short', { count: Number(product.reviews).toLocaleString() }) : ''}
      </Typography.Text>
      <Typography.Text type="secondary" style={{ fontSize: 9 }}>
        BSR: {product.bsr ? Number(product.bsr).toFixed(0) : '—'}
      </Typography.Text>
    </div>
  </Card>
  );
};

const CompetitorModal: React.FC<CompetitorModalProps> = ({
  open,
  niches = [],
  competitors,
  loading,
  onClose,
  onSelect,
  sourceAsin,
  totalFound,
  alreadySelectedAsins = [],
}) => {
  const { t } = useTranslation();
  const [selectedAsins, setSelectedAsins] = useState<Set<string>>(new Set());
  const [viewMode, setViewMode] = useState<ViewMode>('carousel');
  const [sortBy, setSortBy] = useState<SortBy>('clickShare');

  // Initialize with already selected ASINs when modal opens
  React.useEffect(() => {
    if (open) {
      setSelectedAsins(new Set(alreadySelectedAsins));
    }
  }, [open, alreadySelectedAsins]);

  const toggleSelection = (asin: string) => {
    setSelectedAsins((prev) => {
      const next = new Set(prev);
      if (next.has(asin)) {
        next.delete(asin);
      } else {
        next.add(asin);
      }
      return next;
    });
  };

  // For carousel mode: sort products via sortBy
  // For table mode: use table's built-in sorters, don't pre-sort
  const processedNiches = useMemo(() => {
    if (viewMode === 'table') {
      // Don't pre-sort for table mode - let Table handle sorting
      return niches;
    }
    // Sort for carousel mode
    return niches.map((niche) => ({
      ...niche,
      products: sortProducts(niche.products, sortBy),
    }));
  }, [niches, sortBy, viewMode]);

  // Table sorting state (separate from carousel sortBy)
  const [tableSorter, setTableSorter] = useState<{ columnKey: string; order: 'ascend' | 'descend' } | null>(null);

  // Handle table sort change
  const handleTableChange = (_pagination: any, _filters: any, sorter: any) => {
    if (sorter && sorter.columnKey && sorter.order) {
      setTableSorter({ columnKey: sorter.columnKey, order: sorter.order });
    } else {
      setTableSorter(null);
    }
  };

  // Get sort order for a column
  const getTableSortOrder = (columnKey: string): 'ascend' | 'descend' | undefined => {
    return tableSorter?.columnKey === columnKey ? tableSorter.order : undefined;
  };

  const columns: ColumnsType<Competitor> = [
    {
      title: '',
      width: 40,
      render: (_, record) => (
        <Checkbox
          checked={selectedAsins.has(record.asin)}
          onChange={() => toggleSelection(record.asin)}
        />
      ),
    },
    {
      title: t('idea_calc.competitor_modal.col_photo'),
      dataIndex: 'image',
      width: 60,
      render: (url: string) => url ? (
        <ImageWithPreview src={url} width={40} height={40} previewSize={250} />
      ) : null,
    },
    {
      title: 'ASIN',
      dataIndex: 'asin',
      width: 110,
      render: (asin: string) => (
        <Typography.Text copyable={{ text: asin }} style={{ fontSize: 11 }}>
          {asin}
        </Typography.Text>
      ),
    },
    {
      title: t('idea_calc.competitor_modal.col_title'),
      dataIndex: 'title',
      ellipsis: true,
      render: (title: string, record: Competitor) => (
        <Space direction="vertical" size={0}>
          <Tooltip title={title} placement="topLeft" mouseEnterDelay={0.3}>
            <Typography.Text ellipsis style={{ fontSize: 11, maxWidth: 200, cursor: 'default' }}>
              {title || '-'}
            </Typography.Text>
          </Tooltip>
          {record.brand && (
            <Tag color="blue" style={{ fontSize: 10 }}>{record.brand}</Tag>
          )}
        </Space>
      ),
    },
    {
      title: t('idea_calc.competitor_modal.col_price'),
      dataIndex: 'price',
      key: 'price',
      width: 70,
      sorter: (a, b) => (a.price || 0) - (b.price || 0),
      sortOrder: getTableSortOrder('price'),
      render: (price: number) => price ? `$${price.toFixed(2)}` : '-',
    },
    {
      title: '★',
      dataIndex: 'rating',
      key: 'rating',
      width: 50,
      sorter: (a, b) => Number(a.rating || 0) - Number(b.rating || 0),
      sortOrder: getTableSortOrder('rating'),
      render: (rating: string) => rating || '-',
    },
    {
      title: t('idea_calc.competitor_modal.col_reviews'),
      dataIndex: 'reviews',
      key: 'reviews',
      width: 70,
      sorter: (a, b) => Number(a.reviews || 0) - Number(b.reviews || 0),
      sortOrder: getTableSortOrder('reviews'),
      render: (reviews: string) => reviews ? Number(reviews).toLocaleString() : '-',
    },
    {
      title: 'BSR',
      dataIndex: 'bsr',
      key: 'bsr',
      width: 70,
      sorter: (a, b) => Number(a.bsr || 999999) - Number(b.bsr || 999999),
      sortOrder: getTableSortOrder('bsr'),
      render: (bsr: string) => bsr ? Number(bsr).toFixed(0) : '-',
    },
    {
      title: 'Click %',
      dataIndex: 'clickShare',
      key: 'clickShare',
      width: 70,
      sorter: (a, b) => (a.clickShare || 0) - (b.clickShare || 0),
      sortOrder: getTableSortOrder('clickShare'),
      render: (cs: number) => cs ? `${(cs * 100).toFixed(1)}%` : '-',
    },
  ];

  const handleOk = () => {
    // Only add newly selected ASINs (not already in alreadySelectedAsins)
    const newSelections = Array.from(selectedAsins).filter(
      (asin) => !alreadySelectedAsins.includes(asin)
    );
    if (newSelections.length > 0) {
      onSelect(newSelections);
    }
    onClose();
  };

  const handleCancel = () => {
    onClose();
  };

  return (
    <Modal
      title={
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 32 }}>
          <Space>
            <span>{t('idea_calc.competitor_modal.title')}</span>
            {sourceAsin && <Tag color="blue">{sourceAsin}</Tag>}
            <Tag color="green">{t('idea_calc.competitor_modal.products_count', { count: totalFound || competitors.length })}</Tag>
            <Tag color="purple">{niches.length === 1 ? t('idea_calc.competitor_modal.niches_count_one', { count: niches.length }) : t('idea_calc.competitor_modal.niches_count_many', { count: niches.length })}</Tag>
          </Space>
          <Space>
            {viewMode === 'carousel' && (
              <Select
                size="small"
                value={sortBy}
                onChange={setSortBy}
                style={{ width: 130 }}
                options={[
                  { value: 'clickShare', label: t('idea_calc.competitor_modal.sort_relevance') },
                  { value: 'price', label: t('idea_calc.competitor_modal.sort_price') },
                  { value: 'reviews', label: t('idea_calc.competitor_modal.sort_reviews') },
                  { value: 'bsr', label: t('idea_calc.competitor_modal.sort_bsr') },
                  { value: 'rating', label: t('idea_calc.competitor_modal.sort_rating') },
                ]}
              />
            )}
            <Segmented
              size="small"
              value={viewMode}
              onChange={(v) => setViewMode(v as ViewMode)}
              options={[
                { value: 'carousel', icon: <AppstoreOutlined /> },
                { value: 'table', icon: <UnorderedListOutlined /> },
              ]}
            />
          </Space>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      width={1000}
      zIndex={1100}
      styles={{ body: { maxHeight: '70vh', overflowY: 'auto' } }}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          {t('idea_calc.competitor_modal.close_button')}
        </Button>,
        <Button
          key="select"
          type="primary"
          disabled={Array.from(selectedAsins).filter((a) => !alreadySelectedAsins.includes(a)).length === 0}
          onClick={handleOk}
        >
          {t('idea_calc.competitor_modal.add_new_button', { count: Array.from(selectedAsins).filter((a) => !alreadySelectedAsins.includes(a)).length })}
        </Button>,
      ]}
    >
      {loading ? (
        <div style={{ textAlign: 'center', padding: 40 }}>{t('idea_calc.competitor_modal.loading')}</div>
      ) : processedNiches.length > 0 ? (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
          {processedNiches.map((niche) => (
            <div key={niche.nicheId}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
                <Typography.Text strong style={{ fontSize: 13 }}>
                  {niche.nicheTitle}
                </Typography.Text>
                <Tag color="cyan">{t('idea_calc.competitor_modal.niche_count_label', { count: niche.count })}</Tag>
              </div>

              {viewMode === 'carousel' ? (
                <div
                  style={{
                    display: 'flex',
                    gap: 8,
                    overflowX: 'auto',
                    paddingBottom: 8,
                    scrollbarWidth: 'thin',
                  }}
                >
                  {niche.products.map((product) => (
                    <ProductCard
                      key={product.asin}
                      product={product}
                      selected={selectedAsins.has(product.asin)}
                      onToggle={toggleSelection}
                    />
                  ))}
                </div>
              ) : (
                <Table
                  columns={columns}
                  dataSource={niche.products}
                  rowKey="asin"
                  size="small"
                  scroll={{ y: 300 }}
                  pagination={false}
                  onChange={handleTableChange}
                  rowClassName={(record) =>
                    selectedAsins.has(record.asin) ? 'ant-table-row-selected' : ''
                  }
                  onRow={(record) => ({
                    onClick: () => toggleSelection(record.asin),
                    style: { cursor: 'pointer' },
                  })}
                />
              )}
            </div>
          ))}
        </div>
      ) : (
        <Typography.Text type="secondary">{t('idea_calc.competitor_modal.niches_empty')}</Typography.Text>
      )}
    </Modal>
  );
};

export default CompetitorModal;
